import { Fragment, useState, useEffect, useCallback } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { Modal, Button, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';

import ReportList from './ReportList';
import localData from 'utils/localData';
import { useSnackbar } from 'notistack';

import { USER_COMMENT } from 'shared/constants/comment';
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API } from 'shared/constants/apis';
import { IssueStatus, IssueState } from 'shared/constants/issues';
import api from 'utils/api';

import { formatDateTitle } from 'utils/formatTime';
import { AttachmentTitle } from '../AttachmentList/Styles';
import ReportSelection from './ReportSelection';
import {
  generatePDFFromTemplate,
  getPresignedURL,
  loadPdfFromPresignedURL,
} from 'utils/ReportUtils';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { zIndexValues } from 'utils/styles';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { Buffer } from 'buffer';
import logger from 'utils/logger';
import ReportModal from 'components/Report';
import { isDeepEqual } from 'utils/equality';
import {
  LABELS,
  KEY_LABELS,
} from 'components/Report/Templates/DocloudTemplate/constants';

const MODAL_TYPES = {
  SAVE_REPORT: 'SAVE_REPORT',
  PUBLISH_REPORT: 'PUBLISH_REPORT',
  UNSAVED_CHANGES: 'UNSAVED_CHANGES',
  REMOVE_REPORT: 'REMOVE_REPORT',
};

const REPORT_STATUS = {
  NEW: 'NEW',
  SAVED: 'SAVED',
  EDIT: 'EDIT',
  PREVIEW: 'PREVIEW',
};

const propTypes = {
  issue: PropTypes.object.isRequired,
  isDoctor: PropTypes.bool.isRequired,
  isClient: PropTypes.bool.isRequired,
  reloadIssue: PropTypes.func.isRequired,
  openViewer: PropTypes.func.isRequired,
  updateIssue: PropTypes.func.isRequired,
  shouldAllowOpenViewer: PropTypes.bool.isRequired,
};

const log = logger.createLogger('ProjectBoardIssueDetailsReports');

const ProjectBoardIssueDetailsReports = ({
  issue,
  isDoctor,
  isClient,
  reloadIssue,
  openViewer,
  updateIssue,
  shouldAllowOpenViewer,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reportEditorModalOpen, setReportEditorModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState(
    MODAL_TYPES.UNSAVED_CHANGES
  );
  const [reportToPublish, setReportToPublish] = useState(null);
  const [selectReportTypeModalOpen, setSelectReportTypeModalOpen] =
    useState(false);
  const [keyImages, setKeyImages] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [selectedReportTemplate, setSelectedReportTemplate] = useState(null);
  const [reportTemplateContent, setReportTemplateContent] = useState(null);
  const [reportToRemove, setReportToRemove] = useState(null);
  const [reportStatus, setReportStatus] = useState(REPORT_STATUS.NEW);

  useEffect(() => {
    if (!isClient && isDoctor && !issueIsDone) {
      const params = {
        organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
      };
      api.get(`${DOLPHIN_API.TEMPLATES}`, { params }).then((res) => {
        // Check if templates already exist to prevent duplicates
        setTemplateList((currentTemplateList) => {
          const existingTemplateIds = new Set(
            currentTemplateList?.map((template) => template.id) || []
          );
          const newTemplates = res.reportTemplates.filter(
            (template) => !existingTemplateIds.has(template.id)
          );
          if (newTemplates.length > 0) {
            return [...currentTemplateList, ...newTemplates];
          }
          return currentTemplateList;
        });
      });
    }
  }, []);

  const handleClick = () => {
    setSelectReportTypeModalOpen(true);
  };

  const issueIsDone = issue.status === IssueStatus.DONE;

  // The key images should stay for all the reports in the issues until closed
  // It should also be added as a different state
  const handleMessageFromViewer = useCallback(
    async (event) => {
      if (event.type === 'message' && event.data.messageType === 'keyImage') {
        const fileName = event.data.fileName;
        const fileBuffer = await event.data.blob.arrayBuffer();
        try {
          // Adding the image to the keyImages state
          let newKeyImages = [...keyImages];
          newKeyImages.push({
            fileName: fileName,
            fileBuffer: Buffer.from(fileBuffer),
          });
          setKeyImages(newKeyImages);

          // Adding the image to the reportTemplateContent
          const currentKeyImages = reportTemplateContent.settings.find(
            (setting) => setting.label === KEY_LABELS[LABELS.KEY_IMAGES]
          ).value;
          let newKeyInReportImages = [...currentKeyImages];
          newKeyInReportImages.push({
            fileName: fileName,
            fileBuffer: Buffer.from(fileBuffer),
          });
          setReportTemplateContent((prevState) => {
            return {
              ...prevState,
              settings: prevState.settings.map((setting) =>
                setting.label === KEY_LABELS[LABELS.KEY_IMAGES]
                  ? { ...setting, value: newKeyInReportImages }
                  : setting
              ),
            };
          });
          enqueueSnackbar(
            intl.get('issue_details_report_key_image_added_from_viewer'),
            {
              variant: 'success',
              autoHideDuration: 2500,
            }
          );
        } catch (error) {
          log.error('=== Error in handleMessageFromViewer:', error);
        }
      } else if (
        event.type === 'message' &&
        event.data.messageType === 'measurement'
      ) {
        // TODO: Add measurement to the report
      }
    },
    [reportTemplateContent, setKeyImages, enqueueSnackbar]
  );

  const handlePreviewButtonClick = (report) => {
    setReportStatus(REPORT_STATUS.PREVIEW);
    setReportTemplateContent(report.content);
    setReportEditorModalOpen(true);
  };

  const handleDownloadButtonClick = (reportData) => {
    // Check if it's from PDF or not
    if (reportData.content.s3FilePath) {
      // Download the PDF
      getPresignedURL(reportData.content.id, reportData.content.s3FilePath)
        .then((presignedUrl) => {
          loadPdfFromPresignedURL(presignedUrl, reportData.content).then(
            (url) => {
              const link = document.createElement('a');
              link.href = url;
              link.download = reportData.fileName + '.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }
          );
        })
        .catch((error) => {
          log.error('Error in loadPdfFromPresignedURL', error);
          enqueueSnackbar(
            intl.get(
              'issue_details_report_confirm_modal_text_message_download_error_pdf'
            ),
            {
              variant: 'error',
            }
          );
        });
    } else {
      // Download the template
      generatePDFFromTemplate(
        reportData.content.id,
        reportData.content,
        reportData.fileName
      )
        .then((url) => {
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = url;
          link.download = reportData.fileName + '.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          log.error('Error in generatePDFFromTemplate', error);
          enqueueSnackbar(
            intl.get(
              'issue_details_report_confirm_modal_text_message_download_error_template'
            ),
            {
              variant: 'error',
            }
          );
        });
    }
  };

  const handleEditButtonClick = (report) => {
    setSelectedReportTemplate(report);
    setReportTemplateContent(report.content);
    setReportStatus(REPORT_STATUS.EDIT);
    setReportEditorModalOpen(true);
  };

  const handlePublishButtonClick = (report) => {
    setReportToPublish(report);
    setConfirmModalType(MODAL_TYPES.PUBLISH_REPORT);
    setConfirmModalOpen(true);
  };

  const handleRemoveButtonClick = (report) => {
    setReportToRemove(report);
    setConfirmModalType(MODAL_TYPES.REMOVE_REPORT);
    setConfirmModalOpen(true);
  };

  const handleRemoveReport = async () => {
    try {
      const apiVariables = {
        params: {
          organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
          issueId: issue.id,
        },
        data: {
          reportId: reportToRemove.id,
        },
      };
      await api.delete(`${DOLPHIN_API.REPORTS}/delete`, apiVariables);
      await reloadIssue();
      enqueueSnackbar(
        intl.get(
          'issue_details_report_confirm_modal_text_message_remove_success',
          {
            fileName: reportToRemove.fileName,
          }
        ),
        {
          variant: 'success',
          autoHideDuration: 1000,
        }
      );
      setReportToRemove(null);
      setConfirmModalOpen(false);
    } catch (error) {
      log.error('remove report: ', error);
      enqueueSnackbar(
        intl.get('issue_details_report_confirm_modal_text_message_save_error', {
          fileName: reportToRemove.fileName,
        }),
        {
          variant: 'error',
        }
      );
      setReportToRemove(null);
      setConfirmModalOpen(false);
    }
  };

  const saveReportContent = async () => {
    let response;
    try {
      if (selectedReportTemplate.creatorId) {
        const apiVariables = {
          'Content-Type': 'multipart/form-data',
          data: {
            fileName: reportTemplateContent.savedFileName,
            content: reportTemplateContent,
            reportId: selectedReportTemplate.id,
          },
          params: {
            organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
            issueId: issue.id,
          },
        };
        response = await api.post(
          `${DOLPHIN_API.REPORTS}/update`,
          apiVariables
        );
      } else {
        const apiVariables = {
          'Content-Type': 'multipart/form-data',
          data: {
            fileName: reportTemplateContent.savedFileName,
            creatorId: localData.get(USER_DATA.CURRENT_USER_ID),
            isPublished: false,
            content: reportTemplateContent,
          },
          params: {
            organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
            issueId: issue.id,
          },
        };
        response = await api.post(
          `${DOLPHIN_API.REPORTS}/create`,
          apiVariables
        );
      }
      await reloadIssue();
      setSelectedReportTemplate(response.report);
      enqueueSnackbar(
        intl.get('issue_details_report_confirm_modal_text_message_saved', {
          fileName: response.savedFileName,
        }),
        {
          variant: 'success',
          autoHideDuration: 1000,
        }
      );
    } catch (error) {
      log.error('saveReportContent error', error);
      enqueueSnackbar(
        intl.get('issue_details_report_confirm_modal_text_message_save_error', {
          fileName: reportTemplateContent.savedFileName,
        }),
        {
          variant: 'error',
        }
      );
    }
  };

  const handlePublishReport = async () => {
    try {
      const apiVariables = {
        'Content-Type': 'multipart/form-data',
        data: {
          reportId: reportToPublish.id,
        },
        params: {
          organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
          issueId: issue.id,
        },
      };
      //! Updating issue to REPORTREADY
      updateIssue({
        status: IssueStatus.REPORTREADY,
        state: IssueState.NORMAL,
      });
      await api.post(`${DOLPHIN_API.REPORTS}/publish`, apiVariables);
      await reloadIssue();
      setConfirmModalOpen(false);
      enqueueSnackbar(
        intl.get('issue_details_report_confirm_modal_text_message_published'),
        {
          variant: 'success',
        }
      );
    } catch (error) {
      log.error('publish report: ', error);
    }
  };

  // Input the data to the report data
  const initReportContent = (templateData) => {
    const languageCode = localData.get(USER_DATA.PREFFERED_LANGUAGE);
    const tagsDisplayNames =
      issue.tags?.map((tag) => {
        return tag && Object.prototype.hasOwnProperty.call(tag, 'content')
          ? tag.content.displayName[languageCode] ||
              tag.content.displayName['en-US'] ||
              tag.content.displayName[Object.keys(tag.content.displayName)[0]]
          : tag;
      }) || [];

    const tagsText = tagsDisplayNames.join('; ');

    const issueReportContent = {
      [LABELS.ISSUE_TITLE]: issue.title,
      //! ID For shinagawa issue
      [LABELS.ID]: issue.patientName || '',
      [LABELS.ISSUE_DATE]: new Date(issue.createdAt).toISOString(),
      [LABELS.PATIENT_NAME]: issue.patientName || '',
      [LABELS.PATIENT_GENDER]: issue.patientGender || '',
      [LABELS.PATIENT_BIRTHDAY_DATE]: issue.patientBirthdayDate
        ? new Date(issue.patientBirthdayDate).toISOString()
        : new Date().toISOString(),
      [LABELS.DOCTORS_FROM_SRC_ORG]: issue.doctorsInCharge.join(', '),
      [LABELS.DOCTORS_FROM_DST_ORG]: '',
      [LABELS.DEPARTMENT]: issue.department,
      [LABELS.SRC_ORG_NAME]: issue.srcOrgName,
      [LABELS.TAGS]: tagsText,
      [LABELS.DESCRIPTION]: issue.descriptionText || '',
      [LABELS.MEASURES]: '',
      [LABELS.DIAGNOSIS]: '',
      [LABELS.SIGNATURE]: '',
      [LABELS.KEY_IMAGES]: [...keyImages],
      [LABELS.REPORT_DATE]: new Date().toISOString(),
      [LABELS.TITLE]: '',
    };

    // Populate the template with the data
    if (templateData) {
      const updatedSettings = templateData.content.map((setting) => {
        const matchingKey = Object.keys(KEY_LABELS).find(
          (key) =>
            intl.get(KEY_LABELS[key]) === setting.label ||
            KEY_LABELS[key] === setting.label
        );
        if (matchingKey && issueReportContent[matchingKey] !== undefined) {
          return {
            ...setting,
            value: issueReportContent[matchingKey],
          };
        }

        return setting;
      });

      setReportTemplateContent({
        ...templateData,
        savedFileName: `${formatDateTitle(new Date())}`,
        content: updatedSettings,
      });
    }
  };

  const handleReportEditorModalClose = (event, reason) => {
    if (reason === 'backdropClick') {
      event.stopPropagation();
    } else {
      switch (reportStatus) {
        case REPORT_STATUS.PREVIEW:
          setReportEditorModalOpen(false);
          setReportStatus(REPORT_STATUS.NEW);
          return;
        case REPORT_STATUS.EDIT:
        case REPORT_STATUS.SAVED: {
          if (
            !isDeepEqual(reportTemplateContent, selectedReportTemplate.content)
          ) {
            setConfirmModalType(MODAL_TYPES.UNSAVED_CHANGES);
            setConfirmModalOpen(true);
          } else {
            setReportEditorModalOpen(false);
            setReportStatus(REPORT_STATUS.NEW);
          }
          return;
        }
        case REPORT_STATUS.NEW:
          if (
            !isDeepEqual(
              reportTemplateContent.content,
              selectedReportTemplate.content
            )
          ) {
            setConfirmModalType(MODAL_TYPES.UNSAVED_CHANGES);
            setConfirmModalOpen(true);
          } else {
            setReportEditorModalOpen(false);
            setReportStatus(REPORT_STATUS.NEW);
          }
          return;
      }
    }
  };

  const modalData = {
    [MODAL_TYPES.UNSAVED_CHANGES]: {
      title: intl.get(
        'issue_details_report_confirm_modal_text_message_unsaved_changes'
      ),
      onConfirm: () => {
        setReportEditorModalOpen(false);
        setConfirmModalOpen(false);
        setReportStatus(REPORT_STATUS.NEW);
      },
      onCancel: () => {
        setReportEditorModalOpen(true);
        setConfirmModalOpen(false);
      },
      confirmButtonText: intl.get(
        'issue_details_report_confirm_modal_button_discard'
      ),
      cancelButtonText: intl.get(
        'issue_details_report_confirm_modal_button_cancel'
      ),
    },

    // Overwrite the report
    [MODAL_TYPES.SAVE_REPORT]: {
      title: intl.get('issue_details_report_confirm_modal_text_message_save'),
      onConfirm: () => {
        saveReportContent();
        setConfirmModalOpen(false);
        setReportStatus(REPORT_STATUS.SAVED);
      },
      onCancel: () => {
        setReportEditorModalOpen(true);
        setConfirmModalOpen(false);
      },
      confirmButtonText: intl.get(
        'issue_details_report_confirm_modal_button_save'
      ),
      cancelButtonText: intl.get(
        'issue_details_report_confirm_modal_button_cancel'
      ),
    },
    [MODAL_TYPES.PUBLISH_REPORT]: {
      title: intl.get(
        'issue_details_report_confirm_modal_text_message_publish'
      ),
      bodyText: intl.get(
        'issue_details_report_confirm_modal_text_message_publish_warning'
      ),
      onConfirm: handlePublishReport,
      onCancel: () => {
        setConfirmModalOpen(false);
      },
      confirmButtonText: intl.get(
        'issue_details_report_confirm_modal_button_publish'
      ),
      cancelButtonText: intl.get(
        'issue_details_report_confirm_modal_button_cancel'
      ),
    },
    [MODAL_TYPES.REMOVE_REPORT]: {
      title: intl.get('issue_details_report_confirm_modal_text_message_remove'),
      bodyText: intl.get(
        'issue_details_report_confirm_modal_text_message_remove_warning',
        {
          fileName: reportToRemove?.fileName,
        }
      ),
      confirmButtonText: intl.get(
        'issue_details_report_confirm_modal_button_remove'
      ),
      cancelButtonText: intl.get(
        'issue_details_report_confirm_modal_button_cancel'
      ),
      onConfirm: handleRemoveReport,
      onCancel: () => {
        setConfirmModalOpen(false);
      },
    },
  };

  useEffect(() => {
    initReportContent();
    window.addEventListener('message', handleMessageFromViewer);
    return () => {
      window.removeEventListener('message', handleMessageFromViewer);
    };
  }, [handleMessageFromViewer]);

  return (
    <Fragment>
      <Grid container spacing={1} alignItems="center">
        <Grid style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <AttachmentTitle
            style={{
              fontSize: '16px',
            }}>
            📝 {intl.get('issue_details_report_list_label_reports')}
          </AttachmentTitle>
        </Grid>

        {!isClient && isDoctor && !issueIsDone && (
          <Grid
            size={5}
            style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div>
              <Button
                variant="contained"
                startIcon={<SummarizeIcon />}
                sx={{ justifyContent: 'start', color: '#FFF' }}
                onClick={handleClick}>
                {intl.get('issue_details_report_editor_button_open')}
              </Button>
            </div>
          </Grid>
        )}
      </Grid>

      <ReportList
        issue={issue}
        isDoctor={isDoctor}
        isClient={isClient}
        handleDownloadButtonClick={handleDownloadButtonClick}
        handlePreviewButtonClick={handlePreviewButtonClick}
        handleEditButtonClick={handleEditButtonClick}
        handlePublishButtonClick={handlePublishButtonClick}
        handleRemoveButtonClick={handleRemoveButtonClick}
      />

      {/* Select Report */}
      <Modal
        open={selectReportTypeModalOpen}
        onClose={() => setSelectReportTypeModalOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndexValues.modal4,
        }}>
        <Box>
          <ReportSelection
            selectionList={templateList}
            title={intl.get(
              'issue_details_report_editor_button_open_select_template_menu_title'
            )}
            onSelect={(selectedReport) => {
              setSelectReportTypeModalOpen(false);
              setSelectedReportTemplate(selectedReport);
              setReportTemplateContent(selectedReport);
              initReportContent(selectedReport);
              setReportEditorModalOpen(true);
            }}
            onClose={() => setSelectReportTypeModalOpen(false)}
          />
        </Box>
      </Modal>

      {/* Report editor and preview */}
      <Modal
        open={reportEditorModalOpen}
        onClose={handleReportEditorModalClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndexValues.modal4,
        }}>
        <div
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '85vw',
            height: '95vh',
          }}>
          <ReportModal
            openViewer={openViewer}
            keyImages={keyImages}
            setKeyImages={setKeyImages}
            shouldAllowOpenViewer={shouldAllowOpenViewer}
            reportContent={reportTemplateContent}
            setReportContent={setReportTemplateContent}
            saveReportContent={() => {
              setConfirmModalType(MODAL_TYPES.SAVE_REPORT);
              setConfirmModalOpen(true);
            }}
            modalClose={handleReportEditorModalClose}
            isPreview={reportStatus === REPORT_STATUS.PREVIEW}
          />
        </div>
      </Modal>

      <ConfirmModal
        isOpened={confirmModalOpen}
        title={modalData[confirmModalType].title}
        bodyText={modalData[confirmModalType].bodyText}
        onConfirm={modalData[confirmModalType].onConfirm}
        onCancel={modalData[confirmModalType].onCancel}
        confirmButtonText={modalData[confirmModalType].confirmButtonText}
        cancelButtonText={modalData[confirmModalType].cancelButtonText}
      />
    </Fragment>
  );
};

ProjectBoardIssueDetailsReports.propTypes = propTypes;

export default ProjectBoardIssueDetailsReports;
